// UC20 — one medicine code as a record.
//
// The register is one row per code, so opening a row opens the code, not the position it
// shares with its siblings. That is what removed the "Coduri" card an earlier version
// carried: at this grain there is exactly one code, and a list of one is a field.
//
// The layout is the EDITOR's, field for field and row for row — `FormSection` bands with
// a heading on the left, `FormRow` grouping, `FormValue` where the editor has
// `FormField`. A record and its editor are the same page in two states; when they were
// two layouts, a value moved on the screen depending on whether you were reading it or
// changing it, and "where was that field again" became a question about which mode you
// were in.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudTag } from '@egov-moldova/mud-react';
import {
  Breadcrumb,
  EMPTY_CELL,
  PageHeader,
  RowActionsMenu,
  formatDate,
  formatDateTime,
  useToast,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { FormRow, FormSection, FormValue } from '../../components/FormPage/FormPage';
import { fetchMedicineCode, setMedicineCodeActive } from './medicines-api';
import type { MedicineCodeRow } from './medicines-api';
import { MedicineSourceRecord } from './MedicineSourceRecord';
import { usePositionActivation } from './usePositionActivation';

function title(row: MedicineCodeRow): string {
  return row.commercialName || row.dci;
}

export function MedicineCodeDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();
  const positionActivation = usePositionActivation();

  // The code's own switch (UC20-11): the position's is `positionActivation`, and the two
  // are independent — a code can be retired while its position lives, and the reverse.
  const toggleCode = useMutation({
    mutationFn: ({
      medicineId,
      code: target,
      active,
    }: {
      medicineId: string;
      code: string;
      active: boolean;
    }) => setMedicineCodeActive(medicineId, target, active),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-medicine-code'] });
      toast({
        variant: variables.active ? 'success' : 'info',
        title: variables.active
          ? t('medicines.toast.code_activated')
          : t('medicines.toast.code_deactivated'),
        text: variables.code,
      });
    },
    onError: (err) =>
      toast({ variant: 'error', title: t(apiErrorKey(err, 'medicines.error.code_toggle_failed')) }),
  });

  async function onToggleCode(target: MedicineCodeRow): Promise<void> {
    if (target.active) {
      const ok = await confirm({
        title: t('medicines.codes.confirm.deactivate.title'),
        body: t('medicines.codes.confirm.deactivate.body', { code: target.code }),
        confirmLabel: t('confirm.action.deactivate'),
        destructive: true,
      });
      if (!ok) return;
    }
    toggleCode.mutate({
      medicineId: target.medicineId,
      code: target.code,
      active: !target.active,
    });
  }

  const detail = useQuery({
    queryKey: ['admin-medicine-code', code],
    queryFn: () => fetchMedicineCode(code ?? ''),
    enabled: Boolean(code),
  });

  const row = detail.data;
  const listHref = '/system-admin/medicines';
  // Every empty value on this page reads the same, and reads as the register does.
  const show = (value: string): string => value || EMPTY_CELL;

  return (
    <section className="simsm-page simsm-form-page">
      <div className="simsm-form-page__breadcrumb">
        <Breadcrumb
          maxVisible={3}
          label={t('common.breadcrumb.aria_label')}
          crumbs={[
            { label: t('nav.medicines'), to: listHref },
            { label: row ? title(row) : (code ?? '') },
          ]}
        />
      </div>

      <QueryState query={detail}>
        {row ? (
          <>
            <PageHeader
              title={title(row)}
              actions={
                <>
                  {/* The two switches of UC20-11, each said for what it moves: the code
                      alone, or the position and every code it put to sleep with it. */}
                  <RowActionsMenu
                    label={t('medicines.detail.actions', { code: row.code })}
                    size="md"
                    align="end"
                    actions={[
                      {
                        label: row.active
                          ? t('medicines.action.deactivate_code')
                          : t('medicines.action.activate_code'),
                        onSelect: () => void onToggleCode(row),
                        disabled: toggleCode.isPending,
                        danger: row.active,
                      },
                      {
                        label: row.medicineActive
                          ? t('medicines.action.deactivate_position')
                          : t('medicines.action.activate_position'),
                        onSelect: () =>
                          void positionActivation.toggle({
                            id: row.medicineId,
                            active: row.medicineActive,
                            name: title(row),
                          }),
                        disabled: positionActivation.isPending,
                        danger: row.medicineActive,
                      },
                    ]}
                  />
                  <MudButton
                    shape="circular"
                    variant="neutral"
                    size="md"
                    onClick={() => navigate(`/system-admin/medicines/code/${row.code}/history`)}
                  >
                    <MudIcon slot="icon-start" name="clock" size={16} />
                    {t('history.action.open')}
                  </MudButton>
                  <MudButton
                    shape="circular"
                    variant="primary"
                    size="md"
                    onClick={() => navigate(`/system-admin/medicines/code/${row.code}/edit`)}
                  >
                    {t('medicines.action.edit')}
                  </MudButton>
                </>
              }
            />

            {/* The bands are the definition, Wide as the wireframe draws them
                (AdminCoduri.tsx `.admin-form-sections`, 896); the header above
                spans the page (spec 053). */}
            <div className="simsm-page__group">
              <FormSection
                title={t('medicines.section.state')}
                lede={t('medicines.section.state_lede')}
              >
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.column.status')}>
                    <MudTag
                      variant="status"
                      semantic={row.active ? 'success' : 'neutral'}
                      label={
                        row.active ? t('medicines.status.active') : t('medicines.status.inactive')
                      }
                    />
                  </FormValue>
                  <FormValue label={t('medicines.field.authorization_type')}>
                    <MudTag
                      variant="status"
                      semantic={row.kind === 'AUTHORIZED' ? 'success' : 'neutral'}
                      label={t(`medicines.kind.${row.kind}`)}
                    />
                  </FormValue>
                </FormRow>
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.position_status')}>
                    <MudTag
                      variant="status"
                      semantic={row.medicineActive ? 'success' : 'neutral'}
                      label={
                        row.medicineActive
                          ? t('medicines.status.active')
                          : t('medicines.status.inactive')
                      }
                    />
                  </FormValue>
                </FormRow>
              </FormSection>

              <FormSection
                title={t('medicines.section.codes')}
                lede={t('medicines.section.codes_lede')}
              >
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.column.code')}>{row.code}</FormValue>
                  <FormValue label={t('medicines.field.barcode')}>{show(row.barcode)}</FormValue>
                </FormRow>
              </FormSection>

              <FormSection
                title={t('medicines.section.identity')}
                lede={t('medicines.section.identity_lede')}
              >
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.dci')}>{show(row.dci)}</FormValue>
                  <FormValue label={t('medicines.field.form')}>
                    {show(row.pharmaceuticalForm)}
                  </FormValue>
                </FormRow>
                <FormRow columns={3} as="dl">
                  <FormValue label={t('medicines.field.dose')}>{show(row.dose)}</FormValue>
                  <FormValue label={t('medicines.field.volume')}>{show(row.volume)}</FormValue>
                  <FormValue label={t('medicines.field.division')}>{show(row.division)}</FormValue>
                </FormRow>
              </FormSection>

              <FormSection
                title={t('medicines.section.attributes')}
                lede={t('medicines.section.attributes_lede')}
              >
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.commercial_name')}>
                    {show(row.commercialName)}
                  </FormValue>
                  <FormValue label={t('medicines.field.atc')}>{show(row.atcCode)}</FormValue>
                </FormRow>
                {/* The producer takes the whole row here for the reason it does in the
                    editor: its median value is 35 characters and its 95th percentile 82,
                    because both feeds serve it as a `;`-separated list of every production
                    site. */}
                <FormRow as="dl">
                  <FormValue label={t('medicines.field.manufacturer')}>
                    {show(row.manufacturer)}
                  </FormValue>
                </FormRow>
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.holder')}>
                    {show(row.authorizationHolder)}
                  </FormValue>
                  <FormValue label={t('medicines.field.authorization_expiry')}>
                    {row.authorizationExpiresOn
                      ? formatDate(row.authorizationExpiresOn)
                      : EMPTY_CELL}
                  </FormValue>
                </FormRow>
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.country')}>
                    {show(row.manufacturerCountry)}
                  </FormValue>
                  <FormValue label={t('medicines.field.holder_country')}>
                    {show(row.authorizationHolderCountry)}
                  </FormValue>
                </FormRow>
              </FormSection>

              {/* The editor has no counterpart: nothing here is written by hand. */}
              <FormSection
                title={t('medicines.section.system')}
                lede={t('medicines.section.system_lede')}
              >
                <FormRow columns={2} as="dl">
                  <FormValue label={t('medicines.field.origin')}>
                    {t(`medicines.source.${row.source}`)}
                  </FormValue>
                  <FormValue label={t('medicines.field.updated')}>
                    {formatDateTime(row.updatedAtIso)}
                  </FormValue>
                </FormRow>
              </FormSection>

              {/* The row AMDM served for this code, as served (spec 061). */}
              <MedicineSourceRecord
                record={row.sourceRecord ?? null}
                receivedAtIso={row.sourceReceivedAtIso ?? null}
              />
            </div>
          </>
        ) : null}
      </QueryState>
    </section>
  );
}
